import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Modal,
  SpaceBetween,
} from '@cloudscape-design/components';
import { LotStatus } from '@parallel-fluidics/shared/constants';
import updateLotStatus from 'features/lots/api/updateLotStatus';
import PropTypes from 'prop-types';
import { useUserData } from 'providers/userDataProvider';
import React, { useState } from 'react';
import { toast } from 'sonner';

const ActionConfig = Object.freeze({
  markEmpty: {
    title: 'Mark empty',
    status: LotStatus.EMPTY,
    description: 'The bag is used up — its remaining count goes to zero. History and trace links stay intact.',
    reasonRequired: false,
  },
});

/**
 * Confirm dialog for simple single-lot status actions (Mark empty).
 * Quarantine has its own dialog — QuarantineLotModal — because it can
 * recall the whole downstream graph.
 */
export default function LotStatusActionModal({
  lot, action, onClose, onSuccess = () => {},
}) {
  const { user } = useUserData();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const config = ActionConfig[action];

  const handleSubmit = async () => {
    setSubmitError('');
    if (config.reasonRequired && !reason.trim()) {
      setReasonError('Reason is required');
      return;
    }
    setReasonError('');
    setIsSubmitting(true);
    try {
      await updateLotStatus({
        lotId: lot.id,
        status: config.status,
        reason: reason.trim() || null,
        user: { uid: user.id, email: user.email },
      });
      toast.success(`${lot.code} is now ${config.status}`);
      onSuccess();
      onClose();
    } catch (error) {
      setSubmitError(error.message);
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      visible
      onDismiss={onClose}
      header={`${config.title} — ${lot.code}`}
      footer={(
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit} loading={isSubmitting}>
              {config.title}
            </Button>
          </SpaceBetween>
        </Box>
      )}
    >
      <SpaceBetween direction="vertical" size="m">
        {submitError ? <Alert type="error">{submitError}</Alert> : null}
        <Box>{config.description}</Box>
        <FormField label={config.reasonRequired ? 'Reason' : 'Reason — optional'} errorText={reasonError}>
          <Input
            value={reason}
            onChange={({ detail }) => setReason(detail.value)}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

LotStatusActionModal.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  lot: PropTypes.object.isRequired,
  action: PropTypes.oneOf(Object.keys(ActionConfig)).isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};
